import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AppBackButton } from '../components/AppBackButton';

const TermsPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-surface dark:bg-slate-950 text-ink dark:text-slate-200 p-6 md:p-12 font-sans selection:bg-primary/30">
      <div className="max-w-3xl mx-auto space-y-10">
        
        <AppBackButton fallbackTo="/" className="mb-8" />

        <header className="space-y-4 border-b border-black/[0.06] dark:border-white/[0.06] pb-8">
          <h1 className="text-3xl md:text-4xl font-medium tracking-[-0.03em]">TERMOS DE SERVIÇO / TERMS OF SERVICE</h1>
          <p className="text-sm font-medium text-slate-500">Última atualização / Last updated: 1 de junho de 2026</p>
        </header>

        <div className="prose prose-slate dark:prose-invert prose-p:leading-relaxed prose-headings:font-medium max-w-none">
          <h2>VERSÃO EM PORTUGUÊS</h2>
          <p>
            Bem-vindo ao LibriTui. Ao acessar ou usar nosso aplicativo web, aplicativos móveis (iOS e Android) ou qualquer serviço fornecido por nós, você concorda em cumprir e vincular-se a estes Termos de Serviço. Se você não concordar com estes termos, não deverá utilizar o aplicativo.
          </p>

          <h3>1. Escopo do Serviço</h3>
          <p>
            O LibriTui é um aplicativo de leitura, prática de escrita e ferramentas de estudo de idiomas (como flashcards e voz ativa) de alta performance. O aplicativo é desenvolvido, operado e de propriedade da plataforma educacional LibriTui.
          </p>

          <h3>2. Uso de Recursos de Inteligência Artificial e Traduções</h3>
          <p>
            <strong>Natureza Educacional:</strong> O LibriTui utiliza algoritmos avançados e serviços de inteligência artificial (IA) de terceiros para fornecer auxílio em traduções, correções gramaticais e interações de conversação.
          </p>
          <p>
            <strong>Limitação de Responsabilidade:</strong> O usuário reconhece que as ferramentas de IA têm fins estritamente educacionais e complementares. Não garantimos a infalibilidade acadêmica das correções ou precisão contextual absoluta das traduções fornecidas. O LibriTui não será responsável por quaisquer erros, mal-entendidos ou imprecisões decorrentes do uso dessas automações.
          </p>

          <h3>3. Ferramentas de Áudio e Pronúncia (Text-to-Speech)</h3>
          <p>
            O aplicativo pode integrar tecnologias de síntese de voz (Text-to-Speech) para fins de prática de pronúncia. O usuário concorda em utilizar esses recursos exclusivamente dentro dos limites da interface do aplicativo, sendo proibida qualquer tentativa de extração, download em massa ou engenharia reversa dos fluxos de áudio gerados.
          </p>

          <h3>4. Conteúdo Gerado pelo Usuário (Feed da Comunidade)</h3>
          <p>
            Ao utilizar o feed de postagens e a seção de comentários da comunidade no LibriTui, você concorda expressamente em cumprir as seguintes regras:
          </p>
          <ul>
            <li><strong>Responsabilidade Única:</strong> Você é o único e exclusivo responsável por todo o texto, imagens ou interações que publicar.</li>
            <li><strong>Política de Tolerância Zero:</strong> É terminantemente proibida a publicação de conteúdo ofensivo, discurso de ódio, assédio, pornografia, spam, difamação ou qualquer material que viole direitos de terceiros.</li>
            <li><strong>Moderação e Banimento:</strong> O LibriTui reserva-se o direito de remover qualquer conteúdo que viole estas regras sem aviso prévio, bem como suspender ou banir permanentemente a conta do usuário infrator.</li>
          </ul>

          <h3>5. Proteção contra Cópia e Propriedade Intelectual</h3>
          <p>
            Todo o design da interface, arquitetura de rotas, códigos, matrizes estáticas de tradução e textos originais fornecidos pelo aplicativo são propriedade intelectual exclusiva do LibriTui. É expressamente proibido o uso de robôs, spiders, ferramentas de raspagem de dados (web scraping) ou qualquer outro mecanismo automatizado para extrair livros, lições, listas de vocabulário ou dados estruturados do aplicativo sem autorização prévia por escrito.
          </p>

          <h3>6. Assinaturas, Planos Premium e Cancelamentos</h3>
          <p>
            <strong>Processamento por Terceiros:</strong> A comercialização de planos premium ou assinaturas dentro dos aplicativos móveis é processada de forma exclusiva pelas lojas operadoras (Apple App Store e Google Play Store).
          </p>
          <p>
            <strong>Políticas de Reembolso:</strong> Quaisquer solicitações de estorno, cancelamento, reembolso ou problemas de faturamento devem ser mediados e resolvidos diretamente através das plataformas de pagamento da Apple ou do Google, sujeitando-se aos termos e condições de cada respectiva loja.
          </p>

          <h3>7. Alterações nos Termos</h3>
          <p>
            Reservamo-nos o direito de modificar estes termos a qualquer momento. Mudanças significativas serão notificadas através do aplicativo ou da atualização desta página. O uso continuado do aplicativo após as alterações constituirá aceitação dos novos termos.
          </p>

          <hr className="my-12 border-black/[0.06] dark:border-white/[0.06]" />

          <h2>ENGLISH VERSION</h2>
          <p>
            Welcome to LibriTui. By accessing or using our web application, mobile applications (iOS and Android), or any service provided by us, you agree to comply with and be bound by these Terms of Service. If you do not agree to these terms, you must not use the application.
          </p>

          <h3>1. Scope of Service</h3>
          <p>
            LibriTui is a high-performance language learning application featuring reading, writing practice, and study tools (such as flashcards and active voice). The application is developed, operated, and exclusively owned by the LibriTui educational platform.
          </p>

          <h3>2. Use of Artificial Intelligence and Translation Features</h3>
          <p>
            <strong>Educational Purpose:</strong> LibriTui utilizes advanced algorithms and third-party artificial intelligence (AI) services to assist with translations, grammar corrections, and conversational interactions.
          </p>
          <p>
            <strong>Limitation of Liability:</strong> The user acknowledges that AI tools are intended strictly for educational and complementary purposes. We do not guarantee the academic infallibility of corrections or the absolute contextual accuracy of translations. LibriTui shall not be held liable for any errors, misunderstandings, or inaccuracies resulting from the use of these automated features.
          </p>

          <h3>3. Audio and Pronunciation Tools (Text-to-Speech)</h3>
          <p>
            The application may integrate Text-to-Speech (TTS) synthesis technologies for pronunciation practice. The user agrees to use these features exclusively within the boundaries of the application's interface. Any attempt to extract, mass download, or reverse-engineer the generated audio streams is strictly prohibited.
          </p>

          <h3>4. User-Generated Content (Community Feed)</h3>
          <p>
            By using the community posting feed and comment section in LibriTui, you expressly agree to comply with the following rules:
          </p>
          <ul>
            <li><strong>Sole Responsibility:</strong> You are solely and exclusively responsible for any text, images, or interactions you publish.</li>
            <li><strong>Zero-Tolerance Policy:</strong> The publication of objectionable, offensive content, hate speech, harassment, pornography, spam, defamation, or any material that violates third-party rights is strictly prohibited.</li>
            <li><strong>Moderation and Banning:</strong> LibriTui reserves the right to remove any content that violates these rules without prior notice, as well as to suspend or permanently ban the offending user's account.</li>
          </ul>

          <h3>5. Anti-Scraping Protection and Intellectual Property</h3>
          <p>
            All interface design, routing architecture, source code, static translation matrices, and original text provided by the application are the exclusive intellectual property of LibriTui. The use of bots, spiders, web scraping tools, or any other automated mechanisms to extract books, lessons, vocabulary lists, or structured data from the application without prior written authorization is expressly prohibited.
          </p>

          <h3>6. Subscriptions, Premium Plans, and Cancellations</h3>
          <p>
            <strong>Third-Party Processing:</strong> The sale of premium plans or subscriptions within the mobile applications is exclusively processed by the respective store operators (Apple App Store and Google Play Store).
          </p>
          <p>
            <strong>Refund Policies:</strong> Any requests for chargebacks, cancellations, refunds, or billing issues must be mediated and resolved directly through Apple's or Google's payment platforms, subject to the terms and conditions of each respective store.
          </p>

          <h3>7. Changes to the Terms</h3>
          <p>
            We reserve the right to modify these terms at any time. Significant changes will be notified through the application or by updating this page. Continued use of the application after changes are posted constitutes acceptance of the new terms.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
